import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import { COMPANY_DETAILS } from "../pages/appsettings";

// Theme colors (matches app brand green #2d5a3d)
const THEME_GREEN = [45, 90, 61];
const TOTAL_FILL = [233, 240, 235];

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/mainlogo.png";
  });

/**
 * Generic branded report PDF builder (jsPDF + autoTable).
 *
 * Used by every report page so letterhead, title, filter summary,
 * table and footer stay identical across all reports.
 *
 * @param {Object} options
 * @param {string} options.title         - Report title shown top-right (e.g. "STOCK ADJUSTMENTS REPORT")
 * @param {string} [options.subtitle]    - Small subtitle under the title
 * @param {Array}  options.columns       - [{ header, align }] table column headers
 * @param {Array}  options.rows          - Array of row cell arrays (strings/numbers)
 * @param {boolean} [options.includeFilters] - Print the filter summary on the PDF
 * @param {Object} [options.filters]     - { "Label": value } filter summary entries
 * @param {Array}  [options.summary]     - [{ label, value }] summary lines (e.g. financial totals)
 * @param {string} [options.orientation] - "landscape" (default) or "portrait"
 * @param {Array}  [options.totalRow]    - Optional bold totals row appended to the table body
 * @param {Object} [options.columnStyles] - autoTable columnStyles (e.g. { 1: { halign: "right" } })
 * @param {Array}  [options.infoBlocks]  - [{ title, items: [{ label, value }] }] key/value description tables rendered before the main table
 * @param {Array}  [options.sections]    - [{ title, columns, rows, columnStyles }] extra tables rendered after the main table
 * @param {number} [options.recordCount] - Optional record count for the letterhead (defaults to rows.length)
 * @returns {Promise<jsPDF>} The generated jsPDF document (not yet saved)
 */
export const buildReportPDF = async ({
  title,
  subtitle = "",
  columns = [],
  rows = [],
  includeFilters = false,
  filters = {},
  summary = [],
  orientation = "landscape",
  totalRow = null,
  columnStyles = {},
  infoBlocks = [],
  sections = [],
  recordCount = null,
}) => {
  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;

  // ----- Letterhead: logo + company details -----
  const logo = await loadLogo();
  const logoX = margin;
  const logoY = 13;
  const logoSize = 20;

  if (logo) {
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  } else {
    doc.setFillColor(...THEME_GREEN);
    doc.roundedRect(logoX, logoY, logoSize, logoSize, 2, 2, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("AF", logoX + logoSize / 2, logoY + logoSize / 2 + 1, { align: "center" });
  }

  const textX = logoX + logoSize + 7;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(COMPANY_DETAILS.name.toUpperCase(), textX, 18);

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY_DETAILS.address, textX, 23);
  doc.text(`Tel: ${COMPANY_DETAILS.phone} | Mobile: ${COMPANY_DETAILS.mobile}`, textX, 27);
  doc.text(`Email: ${COMPANY_DETAILS.email} | Web: ${COMPANY_DETAILS.website}`, textX, 31);

  // ----- Document title (right aligned) -----
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...THEME_GREEN);
  doc.text(title, pageWidth - margin, 19, { align: "right" });

  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, pageWidth - margin, 25, { align: "right" });
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${dayjs().format("YYYY-MM-DD HH:mm")}`, pageWidth - margin, subtitle ? 30 : 25, { align: "right" });
  doc.text(`Records: ${recordCount != null ? recordCount : rows.length}`, pageWidth - margin, subtitle ? 34 : 29, { align: "right" });

  // ----- Divider -----
  doc.setDrawColor(...THEME_GREEN);
  doc.setLineWidth(0.9);
  doc.line(margin, 38, pageWidth - margin, 38);

  // ----- Filters summary -----
  let startY = 43;
  const filterParts = Object.entries(filters)
    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${v}`);

  if (includeFilters && filterParts.length > 0) {
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);
    doc.text(filterParts.join("   |   "), margin, startY);
    startY += 6;
  }

  // ----- Summary lines (financial totals etc.) -----
  if (summary.length > 0) {
    let sy = startY;
    doc.setFontSize(9);
    summary.forEach((s) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...THEME_GREEN);
      doc.text(s.label, margin, sy);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 60);
      doc.text(String(s.value), margin + 62, sy);
      sy += 5.2;
    });
    startY = sy + 2;
  }

  // ----- Reusable page furniture -----
  const drawFooter = () => {
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Page ${doc.internal.getNumberOfPages()}`,
      pageWidth - margin,
      pageHeight - 8,
      { align: "right" }
    );
    doc.text(COMPANY_DETAILS.name, margin, pageHeight - 8);
  };

  const drawSectionTitle = (text, y) => {
    if (!text) return y;
    if (y > pageHeight - 34) {
      doc.addPage();
      y = margin + 4;
    }
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...THEME_GREEN);
    doc.text(text, margin, y);
    return y + 4.5;
  };

  // ----- Info blocks (key/value descriptions, two pairs per row) -----
  infoBlocks.forEach((block) => {
    startY = drawSectionTitle(block.title, startY);
    const body = [];
    const items = (block.items || []).filter(Boolean);
    for (let i = 0; i < items.length; i += 2) {
      const a = items[i];
      const b = items[i + 1];
      body.push([
        a.label,
        String(a.value ?? "-"),
        b ? b.label : "",
        b ? String(b.value ?? "-") : "",
      ]);
    }
    if (body.length > 0) {
      autoTable(doc, {
        body,
        startY,
        theme: "grid",
        margin: { left: margin, right: margin },
        styles: { fontSize: 8.5, cellPadding: 2, overflow: "linebreak" },
        columnStyles: {
          0: { fontStyle: "bold", fillColor: TOTAL_FILL, cellWidth: 42 },
          2: { fontStyle: "bold", fillColor: TOTAL_FILL, cellWidth: 42 },
        },
        didDrawPage: drawFooter,
      });
      startY = doc.lastAutoTable.finalY + 9;
    } else {
      startY += 2;
    }
  });

  // ----- Main table -----
  if (columns.length > 0) {
    const head = [columns.map((c) => c.header)];
    const body = rows.map((r) => (Array.isArray(r) ? r : r.cells));
    if (totalRow) body.push(totalRow);

    const bodyRowCount = body.length;

    autoTable(doc, {
      head,
      body,
      startY,
      theme: "grid",
      margin: { left: margin, right: margin },
      headStyles: { fillColor: THEME_GREEN, fontSize: 9, halign: "center" },
      styles: { fontSize: 8.5, cellPadding: 2, overflow: "linebreak" },
      columnStyles,
      didParseCell: (data) => {
        // Bold highlighted total row (last body row)
        if (totalRow && data.section === "body" && data.row.index === bodyRowCount - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = TOTAL_FILL;
        }
      },
      didDrawPage: drawFooter,
    });
    startY = doc.lastAutoTable.finalY + 9;
  } else {
    startY += 2;
  }

  // ----- Extra sections (multi-table reports) -----
  sections.forEach((section) => {
    startY = drawSectionTitle(section.title, startY);
    autoTable(doc, {
      head: [(section.columns || []).map((c) => c.header)],
      body: section.rows || [],
      startY,
      theme: "grid",
      margin: { left: margin, right: margin },
      headStyles: { fillColor: THEME_GREEN, fontSize: 9, halign: "center" },
      styles: { fontSize: 8.5, cellPadding: 2, overflow: "linebreak" },
      columnStyles: section.columnStyles || {},
      didDrawPage: drawFooter,
    });
    startY = doc.lastAutoTable.finalY + 9;
  });

  return doc;
};
